/**
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You
 * may not use this file except in compliance with the License. A copy of
 * the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is
 * distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the License for the specific
 * language governing permissions and limitations under the License.
 */
import "./StepUpAuth.css";
import {
  usePasswordless,
  useLocalUserCache,
  useAwaitableState,
} from "amazon-cognito-passwordless-auth/react";
import { useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/auth-context";
import { AppLayout } from "../../../components/AppLayout";

function StepUpAuth() {
  const { setShowStepUpAuth } = useAuthContext();
  const {
    signInStatus,
    tokensParsed,
    authenticateWithFido2,
    stepUpAuthenticationWithSmsOtp,
    busy,
    fido2Credentials,
  } = usePasswordless();
  const { currentUser } = useLocalUserCache();
  const [consentId, setConsentId] = useState<string>("");
  const [lastError, setLastError] = useState<Error>();
  const [stepUpAuthIdToken, setStepUpAuthIdToken] = useState("");
  const [smsOtpPhoneNumber, setSmsOtpPhoneNumber] = useState("");
  const [isWrongSmsOtp, setIsWrongSmsOtp] = useState<boolean>(false);
  const showSmsOtpInput = !!smsOtpPhoneNumber;
  const [smsOtp, setSmsOtp] = useState("");
  const {
    awaitable: awaitableSmsOtp,
    resolve: resolveSmsOtp,
    awaited: awaitedSmsOtp,
    reject: cancelWaitingForSmsOtp,
  } = useAwaitableState(smsOtp);

  function handleStepUpAuthentication(
    stepUpAuthFn: () => Promise<{ idToken: string }>
  ) {
    setLastError(undefined);
    setStepUpAuthIdToken("");
    stepUpAuthFn()
      .then(({ idToken }) => setStepUpAuthIdToken(idToken))
      .catch(setLastError);
  }

  useEffect(() => {
    if (signInStatus !== "SIGNED_IN") {
      setLastError(undefined);
      setConsentId("");
      setSmsOtpPhoneNumber("");
      setIsWrongSmsOtp(false);
      setStepUpAuthIdToken("");
    }
  }, [signInStatus]);

  if (!tokensParsed) return null;

  return (
    <AppLayout
      breadcrumb={{ parent: 'Home', current: 'Step Up Auth' }}
      currentPage="stepupauth"
    >
      <div className="container-fluid py-2">
        <div className="row">
          <div className="col-12 mt-3">
            <div className="card overflow-hidden">
              <div className="card-header p-0 position-relative z-index-2">
                <div className="bg-deep-blue py-3 pe-1">
                  <h4 className="text-white font-weight-bolder text-center mt-2">Step Up Authentication</h4>
                  <p className="text-white text-center mb-0">Additional security verification required</p>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-12">
                    <div className="step-up-auth-container">
                      <div>
                        Enter a Consent ID. You can make one up,
                        <br /> but let's pretend there's a transaction backend that
                        generated it.
                        <br /> (Also see:{" "}
                        <a
                          href="https://github.com/aws-samples/amazon-cognito-passwordless-auth/blob/main/SMS-OTP-STEPUP.md#step-up-authentication-with-sms-one-time-password"
                          target="_blank"
                          rel="noreferrer"
                        >
                          explanation of the step up procedure
                        </a>
                        )
                      </div>
                      <div className="input-group input-group-outline my-3">

                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Consent ID"
                          value={consentId}
                          onChange={(e) => setConsentId(e.target.value)}
                          disabled={busy}
                          autoFocus
                          onFocus={(e) => e.target.parentElement?.classList.add('focused')}
                          onBlur={(e) => e.target.parentElement?.classList.remove('focused')}
                        />
                      </div>
                      <div>
                        Next, initiate Step Up authentication. Choose either method:
                      </div>
                      <div className="d-flex gap-3 mt-4">
                        <button
                          title={
                            !fido2Credentials?.length
                              ? "To use WebAuthn, first add face or touch unlock"
                              : undefined
                          }
                          disabled={!fido2Credentials?.length || !consentId || busy}
                          onClick={() =>
                            handleStepUpAuthentication(
                              () =>
                                authenticateWithFido2({
                                  username: tokensParsed.idToken["cognito:username"],
                                  credentials: currentUser?.credentials,
                                  clientMetadata: { consent_id: consentId },
                                }).signedIn
                            )
                          }
                          className="btn bg-deep-blue btn-lg flex-fill"
                        >
                          WebAuthn
                        </button>
                        <button
                          disabled={!consentId || busy}
                          onClick={() => {
                            setIsWrongSmsOtp(false);
                            handleStepUpAuthentication(() =>
                              stepUpAuthenticationWithSmsOtp({
                                username: tokensParsed.idToken["cognito:username"],
                                smsMfaCode: (phoneNumber: string, attempt: number) => {
                                  setSmsOtpPhoneNumber(phoneNumber);
                                  setIsWrongSmsOtp(attempt > 1);
                                  return awaitableSmsOtp();
                                },
                                clientMetadata: { consent_id: consentId },
                              }).signedIn.finally(() => {
                                setSmsOtp("");
                                setSmsOtpPhoneNumber("");
                              })
                            );
                          }}
                          className="btn bg-deep-blue btn-lg flex-fill"
                        >
                          SMS OTP
                        </button>
                      </div>
                      {showSmsOtpInput && (
                        <>
                          <div>Enter the OTP code we've sent to {smsOtpPhoneNumber}:</div>
                          <form
                            className="d-flex gap-3 mt-3"
                            onSubmit={(e) => {
                              e.preventDefault();
                              setIsWrongSmsOtp(false);
                              resolveSmsOtp();
                            }}
                          >
                            <div className="input-group input-group-outline flex-fill">
                              <label className="form-label">OTP Code</label>
                              <input
                                id="sms-otp"
                                type="text"
                                className="form-control"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                autoComplete="one-time-code"
                                placeholder="Enter 6-digit OTP"
                                autoFocus
                                title="OTP Code: should be 6 numbers"
                                value={smsOtp}
                                onChange={(e) => setSmsOtp(e.target.value)}
                                disabled={!!awaitedSmsOtp && !isWrongSmsOtp}
                                maxLength={6}
                                onFocus={(e) => e.target.parentElement?.classList.add('focused')}
                                onBlur={(e) => e.target.parentElement?.classList.remove('focused')}
                              />
                            </div>
                            <button
                              type="submit"
                              disabled={
                                smsOtp.length < 6 || (!!awaitedSmsOtp && !isWrongSmsOtp)
                              }
                              className="btn bg-deep-blue btn-lg flex-fill"
                            >
                              Submit
                            </button>
                            <button
                              disabled={!!awaitedSmsOtp && !isWrongSmsOtp}
                              type="button"
                              onClick={() =>
                                cancelWaitingForSmsOtp(
                                  new Error("SMS OTP step up authentication cancelled")
                                )
                              }
                              className="btn bg-deep-blue btn-lg flex-fill "
                            >
                              Cancel
                            </button>
                          </form>
                        </>
                      )}
                      {stepUpAuthIdToken && (
                        <div>
                          <div>
                            Good job, you stepped up authentication.
                            <br />
                            Your Consent ID was added to your ID-token:{" "}
                            <a
                              href={`https://jwtinspector.kevhak.people.aws.dev/inspect#token=${stepUpAuthIdToken}&tab=payload`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              view
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="step-up-auth-error">
                      {!lastError && awaitedSmsOtp && isWrongSmsOtp && (
                        <div>That's not the right code</div>
                      )}
                      {lastError && <div>{lastError.message}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default StepUpAuth;
